import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import Lottie from 'react-lottie';
import Switch, { switchClasses } from '@mui/joy/Switch';
import { FaEye, FaEdit, FaTrashAlt } from 'react-icons/fa';

import { ContainerStudies } from './style.js';
import Loading from '../../../assets/loading.json';
import Menu from '../../../components/Menu/index.js';
import Footer from '../../../components/Footer/index.js';
import AuthGuard from '../../../components/AuthGuard/index.js';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';

import api from '../../../services/api.js';

import ButtonDefault from '../../../components/ButtonDefault/index.js';
import SelectDefault from '../../../components/SelectDefault/index.js';
import InputDefault from '../../../components/InputDefault/index.js';
import GenericActionMenu from '../../../components/GenericActionMenu/index.js';
import ModalDelete from '../../../components/Modals/ModalDelete/index.js';
import ModalAdd from '../../../components/Modals/ModalAdd/index.js';
import ModalEdit from '../../../components/Modals/ModalEdit/index.js';
import ModalViewChannel from '../../../components/Modals/ModalViewChannel/index.js';

const SalesChannelList = () => {
  document.title = 'Enermerco - Canais de Venda';

  const [isLoading, setIsLoading] = useState(true);
  const [salesChannelList, setSalesChannelList] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
  });

  // Filtros
  const [id, setId] = useState('');
  const [situation, setSituation] = useState('A');
  const [name, setName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Modal states
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalViewChannel, setModalViewChannel] = useState(false);
  const [modalChannelDesc, setModalChannelDesc] = useState('');

  // Modal form data
  const [modalName, setModalName] = useState('');
  const [modalCpfCnpj, setModalCpfCnpj] = useState('');
  const [modalPhone, setModalPhone] = useState('');
  const [modalEmail, setModalEmail] = useState('');
  const [modalContact, setModalContact] = useState('');
  const [modalSituation, setModalSituation] = useState('A');

  const loadingAnimation = {
    loop: true,
    autoplay: true,
    animationData: Loading
  };

  // Mask functions
  const maskPhone = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    } else {
      return cleaned.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    }
  };

  const maskCpfCnpj = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 11) {
      // CPF: 000.000.000-00
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4').replace(/-$/, '');
    } else {
      // CNPJ: 00.000.000/0000-00
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5').replace(/-$/, '');
    }
  };

  const applyFilter = (id, situation, name, cpfCnpj, phone, email, page = 1) => {
    setIsLoading(true);
    let url = `sales_channel?page=${page}`;
    
    if (situation !== 'T' && situation !== '') {
      url += `&situation=${situation}`;
    }
    if (id !== '') {
      url += `&id=${id}`;
    }
    if (name !== '') {
      url += `&name=${name}`;
    }
    if (cpfCnpj !== '') {
      url += `&cpf_cnpj=${cpfCnpj}`;
    }
    if (phone !== '') {
      url += `&phone=${phone}`;
    }
    if (email !== '') {
      url += `&email=${email}`;
    }

    api
      .get(url)
      .then((response) => {
        setSalesChannelList(response.data.data);
        setPagination(response.data.pagination);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const searchFilter = useCallback(() => {
    applyFilter(id, situation, name, cpfCnpj, phone, email);
  }, [id, situation, name, cpfCnpj, phone, email]);

  const cleanFields = useCallback(() => {
    setId('');
    setSituation('A');
    setName('');
    setCpfCnpj('');
    setPhone('');
    setEmail('');
    applyFilter('', 'A', '', '', '', '', 1);
  }, []);

  const goToPage = useCallback((page) => {
    applyFilter(id, situation, name, cpfCnpj, phone, email, page);
  }, [id, situation, name, cpfCnpj, phone, email]);

  const goToFirstPage = useCallback(() => {
    goToPage(1);
  }, [goToPage]);

  const goToPreviousPage = useCallback(() => {
    if (pagination.current_page > 1) {
      goToPage(pagination.current_page - 1);
    }
  }, [pagination.current_page, goToPage]);

  const goToNextPage = useCallback(() => {
    if (pagination.has_more_pages) {
      goToPage(pagination.current_page + 1);
    }
  }, [pagination.has_more_pages, pagination.current_page, goToPage]);

  const goToLastPage = useCallback(() => {
    goToPage(pagination.last_page);
  }, [pagination.last_page, goToPage]);

  const deleteSalesChannel = useCallback((id) => {
    api
      .delete(`sales_channel?id=${id}`)
      .then((response) => {
        setModalDelete(false);
        if (response.status === 200) {
          toast.success('Canal de venda removido com sucesso!');
        } else {
          toast.error(response.data.message);
        }
        cleanFields();
      })
      .catch((error) => {
        cleanFields();
        setModalDelete(false);
        toast.error('Erro ao remover canal de venda');
      });
  }, [cleanFields]);

  const addSalesChannel = useCallback((
    modalText,
    _modalNumber1,
    _modalNumber2,
    _modalNumber3,
    _id,
    _date,
    modalText2,
    _type,
    _modalTextPassword,
    modalNrFone,
    modalNrCpfCnpj,
    modalContact
  ) => {
    const data = {
      NM_CANAL: modalText.toUpperCase(),
      NR_CPFCNPJ: modalNrCpfCnpj?.replace(/\D/g, ''),
      NR_FONE: modalNrFone?.replace(/\D/g, ''),
      DS_EMAIL: modalText2,
      NM_CONTATO: modalContact.toUpperCase()
    };

    api
      .post(`sales_channel`, data)
      .then((response) => {
        setModalAdd(false);
        if (response.status === 201) {
          toast.success('Canal de venda adicionado com sucesso!');
        } else {
          toast.error(response.data.message);
        }
        cleanFields();
      })
      .catch((error) => {
        setModalAdd(false);
        cleanFields();
        toast.error('Erro ao adicionar canal de venda: ' + error.response.data.message);
      });
  }, [cleanFields]);

  const editSalesChannel = useCallback((
    modalText,
    _modalNumber1,
    _modalNumber2,
    _modalNumber3,
    id,
    modalStatus,
    _date,
    _modalTextPassword,
    modalNrFone,
    modalNrCpfCnpj,
    modalContact,
    modalText2
  ) => {
    const data = {
      NM_CANAL: modalText.toUpperCase(),
      NR_CPFCNPJ: modalNrCpfCnpj?.replace(/\D/g, ''),
      NR_FONE: modalNrFone?.replace(/\D/g, ''),
      DS_EMAIL: modalText2,
      NM_CONTATO: modalContact.toUpperCase(),
      IN_SITUACAO: modalStatus
    };

    api
      .put(`sales_channel?id=${id}`, data)
      .then((response) => {
        setModalEdit(false);
        if (response.status === 201) {
          toast.success('Canal de venda alterado com sucesso!');
        } else {
          toast.error(response.data.message);
        }
        // Atualizar a página atual após edição
        applyFilter(id, situation, name, cpfCnpj, phone, email, pagination.current_page);
      })
      .catch((error) => {
        setModalEdit(false);
        applyFilter(id, situation, name, cpfCnpj, phone, email, pagination.current_page);
        toast.error('Erro ao alterar canal de venda');
      });
  }, [situation, name, cpfCnpj, phone, email, pagination.current_page]);

  useEffect(() => {
    applyFilter('', 'A', '', '', '', '', 1);
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const updateChannelStatus = (status, channel) => {
    api
      .put(`sales_channel?id=${channel.ID_DM017}`, {
        NM_CANAL: channel.NM_CANAL,
        NR_CPFCNPJ: channel.NR_CPFCNPJ,
        NR_FONE: channel.NR_FONE,
        DS_EMAIL: channel.DS_EMAIL,
        NM_CONTATO: channel.NM_CONTATO,
        IN_SITUACAO: status ? 'A' : 'I'
      })
      .then((response) => {
        if (response.status === 201) {
          toast.success('Status do canal alterado com sucesso!');
        } else {
          toast.error(response.data.message);
        }
        // Atualizar a página atual após mudança de status
        applyFilter(id, situation, name, cpfCnpj, phone, email, pagination.current_page);
      })
      .catch((error) => {
        toast.error('Erro ao alterar status do canal');
      });
  };

  return (
    <>
      <AuthGuard />
      <Menu />
      <Container fluid>
        <ContainerStudies>
          <div className="page-header">
            <h2>Canais de Venda</h2>
            <p className="text-muted">
              Gerencie supermercados, conveniências e outros estabelecimentos parceiros
            </p>
          </div>

          {/* Seção de Filtros */}
          <div className="filter-section">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 style={{ 
                  color: 'var(--primary-color)', 
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: 0
                }}>
                  Filtros de Busca
                </h5>
                <ButtonDefault
                  Width={'150px'}
                  Margin={'0'}
                  Text="Adicionar Canal"
                  onClick={() => {
                    setModalAdd(true);
                  }}
                />
              </div>
              <Row>
                <Col xs={12} md={3}>
                  <SelectDefault
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'Situação'}
                    value={`${situation}`}
                    onChange={(e) => {
                      setSituation(e.target.value);
                    }}>
                    <option value="A">ATIVO</option>
                    <option value="I">INATIVO</option>
                    <option value="T">TODOS</option>
                  </SelectDefault>
                </Col>
                <Col xs={12} md={2}>
                  <InputDefault
                    placeholder="Código do Canal"
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'Código'}
                    value={`${id}`}
                    onChange={(e) => setId(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        searchFilter();
                      }
                    }}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <InputDefault
                    placeholder="Nome do Canal"
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'Nome'}
                    value={`${name}`}
                    onChange={(e) => setName(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        searchFilter();
                      }
                    }}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <InputDefault
                    placeholder="E-mail do Canal"
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'E-mail'}
                    value={`${email}`}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        searchFilter();
                      }
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={3}>
                  <InputDefault
                    placeholder="Telefone do Canal"
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'Telefone'}
                    value={maskPhone(phone)}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        searchFilter();
                      }
                    }}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <InputDefault
                    placeholder="CPF/CNPJ do Canal"
                    Width={'100%'}
                    Margin={'10px 0px 15px 0px'}
                    Text={'CPF/CNPJ'}
                    value={maskCpfCnpj(cpfCnpj)}
                    onChange={(e) => setCpfCnpj(e.target.value.replace(/\D/g, ''))}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        searchFilter();
                      }
                    }}
                  />
                </Col>
              </Row>
              <div className="d-flex gap-3">
                <ButtonDefault
                  Width={'150px'}
                  Margin={'20px 0px 0px 0px'}
                  Text="Buscar"
                  onClick={searchFilter}
                />
                <ButtonDefault
                  Width={'150px'}
                  Margin={'20px 0px 0px 0px'}
                  Text="Limpar"
                  onClick={() => {
                    cleanFields();
                  }}
                />
              </div>
            </div>

          {/* Seção da Tabela */}
          <div className="table-section">
            {isLoading ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: '400px',
                backgroundColor: 'var(--card-background)',
                borderRadius: 'var(--border-radius)',
                margin: '20px 0'
              }}>
                <Lottie options={loadingAnimation} style={{ width: '200px', height: '200px' }} />
                <h5 style={{ color: 'var(--text-color)', marginTop: '20px' }}>
                  Carregando Canais de Venda...
                </h5>
              </div>
            ) : salesChannelList.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: '400px',
                backgroundColor: 'var(--card-background)',
                borderRadius: 'var(--border-radius)',
                margin: '20px 0',
                padding: '40px',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '48px', 
                  color: 'var(--text-muted)', 
                  marginBottom: '20px' 
                }}>
                  📋
                </div>
                <h5 style={{ 
                  color: 'var(--text-color)', 
                  marginBottom: '15px',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  Nenhum Canal de Venda encontrado
                </h5>
                <p style={{ 
                  color: 'var(--text-muted)', 
                  marginBottom: '30px',
                  maxWidth: '400px',
                  lineHeight: '1.5'
                }}>
                  Não encontramos canais de venda com os filtros aplicados. 
                  Tente ajustar os filtros de busca ou cadastre um novo canal.
                </p>
                <div className="d-flex gap-3">
                  <ButtonDefault
                    Width={'160px'}
                    Margin={'0'}
                    Text="Limpar Filtros"
                    onClick={cleanFields}
                  />
                  <ButtonDefault
                    Width={'160px'}
                    Margin={'0'}
                    Text="Adicionar Canal"
                    onClick={() => setModalAdd(true)}
                  />
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover variant="light">
              <thead>
                <tr>
                  <th style={{ width: '5%', textAlign: 'right' }}>Código</th>
                  <th style={{ width: '20%' }}>Nome do Canal</th>
                  <th style={{ width: '15%' }}>CPF/CNPJ</th>
                  <th style={{ width: '10%' }}>Telefone</th>
                  <th style={{ width: '15%' }}>E-mail</th>
                  <th style={{ width: '15%' }}>Contato</th>
                  <th style={{ width: '5%' }}>Situação</th>
                  <th style={{ width: '5%' }}>Alteração</th>
                  <th style={{ width: '10%', textAlign: 'center' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {salesChannelList.map((channel) => (
                  <tr key={channel.ID_DM017}>
                    <td style={{ textAlign: 'right' }}>{channel.ID_DM017}</td>
                    <td>{channel.NM_CANAL}</td>
                    <td>{maskCpfCnpj(channel.NR_CPFCNPJ)}</td>
                    <td>{maskPhone(channel.NR_FONE)}</td>
                    <td>{channel.DS_EMAIL}</td>
                    <td>{channel.NM_CONTATO}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Switch
                        color={channel.IN_SITUACAO === 'A' ? 'success' : 'danger'}
                        checked={channel.IN_SITUACAO === 'A'}
                        onChange={(event) => updateChannelStatus(event.target.checked, channel)}
                        sx={{
                          '--Switch-thumbSize': '16px',
                          '--Switch-trackWidth': '40px',
                          '--Switch-trackHeight': '24px',
                          '--Switch-trackBackground': '#EE5E52',
                          '&:hover': {
                            '--Switch-trackBackground': '#EE5E52'
                          },
                          [`&.${switchClasses.checked}`]: {
                            '--Switch-trackBackground': '#5CB176',
                            '&:hover': {
                              '--Switch-trackBackground': '#5CB176'
                            }
                          }
                        }}
                      />
                    </td>
                    <td>{formatDate(channel.DT_ALTERACAO)}</td>
                    <td className="action-menu-container" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <GenericActionMenu
                        actions={[
                          {
                            key: 'view',
                            label: 'Visualizar',
                            icon: FaEye,
                            onClick: () => {
                              setId(channel.ID_DM017);
                              setModalChannelDesc(channel.NM_CANAL);
                              setModalViewChannel(true);
                            }
                          },
                          { type: 'divider' },
                          {
                            key: 'edit',
                            label: 'Editar',
                            icon: FaEdit,
                            onClick: () => {
                              setId(channel.ID_DM017);
                              setModalName(channel.NM_CANAL);
                              setModalCpfCnpj(channel.NR_CPFCNPJ || '');
                              setModalPhone(channel.NR_FONE || '');
                              setModalEmail(channel.DS_EMAIL || '');
                              setModalContact(channel.NM_CONTATO || '');
                              setModalSituation(channel.IN_SITUACAO);
                              setModalEdit(true);
                            }
                          },
                          {
                            key: 'delete',
                            label: 'Excluir',
                            icon: FaTrashAlt,
                            variant: 'danger',
                            onClick: () => {
                              setId(channel.ID_DM017);
                              setModalChannelDesc(channel.NM_CANAL);
                              setModalDelete(true);
                            }
                          }
                        ]}
                        buttonLabel={`Ações para ${channel.NM_CANAL}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Controles de Paginação */}
            {pagination.total > 0 && salesChannelList.length > 0 && (
              <div className="pagination-section mt-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="pagination-info">
                    <span style={{ color: 'var(--text-color)', fontSize: '14px' }}>
                      Exibindo {pagination.from} a {pagination.to} de {pagination.total} registros
                    </span>
                  </div>
                  
                  <div className="pagination-controls d-flex align-items-center gap-2">
                    <ButtonDefault
                      Width={'80px'}
                      Margin={'0'}
                      Text="<<"
                      onClick={goToFirstPage}
                      disabled={pagination.current_page === 1}
                      style={{ 
                        opacity: pagination.current_page === 1 ? 0.5 : 1,
                        cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <ButtonDefault
                      Width={'80px'}
                      Margin={'0'}
                      Text="<"
                      onClick={goToPreviousPage}
                      disabled={pagination.current_page === 1}
                      style={{ 
                        opacity: pagination.current_page === 1 ? 0.5 : 1,
                        cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer'
                      }}
                    />
                    
                    <span style={{ 
                      margin: '0 15px', 
                      color: 'var(--text-color)',
                      fontWeight: 'var(--font-weight-medium)',
                      fontSize: '14px'
                    }}>
                      Página {pagination.current_page} de {pagination.last_page}
                    </span>
                    
                    <ButtonDefault
                      Width={'80px'}
                      Margin={'0'}
                      Text=">"
                      onClick={goToNextPage}
                      disabled={!pagination.has_more_pages}
                      style={{ 
                        opacity: !pagination.has_more_pages ? 0.5 : 1,
                        cursor: !pagination.has_more_pages ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <ButtonDefault
                      Width={'80px'}
                      Margin={'0'}
                      Text=">>"
                      onClick={goToLastPage}
                      disabled={!pagination.has_more_pages}
                      style={{ 
                        opacity: !pagination.has_more_pages ? 0.5 : 1,
                        cursor: !pagination.has_more_pages ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
              </div>
            )}
          </div>
        </ContainerStudies>
        <ToastContainer />
          <ModalViewChannel
            modalOpen={modalViewChannel}
            handleClose={() => setModalViewChannel(false)}
            channelId={id}
            channelName={modalChannelDesc}
          />
          <ModalDelete
            modalOpen={modalDelete}
            handleClose={() => setModalDelete(false)}
            handleConfirm={deleteSalesChannel}
            handleCancel={() => setModalDelete(false)}
            modalTitleText={`Deseja excluir o Canal de Venda #${id}?`}
            modalText={modalChannelDesc}
            id={id}
          />
          <ModalAdd
            modalOpen={modalAdd}
            handleClose={() => setModalAdd(false)}
            handleConfirm={(modalText, modalNumber1, modalNumber2, modalNumber3, id, date, modalText2, type, modalTextPassword) => 
              addSalesChannel(modalText, modalNumber1, modalNumber2, modalNumber3, id, date, modalText2, type, modalTextPassword, modalPhone, modalCpfCnpj, modalContact)
            }
            handleCancel={() => setModalAdd(false)}
            modalTitleText={'Adicionar Canal de Venda'}
            visibleText={true}
            visibleText2={true}
            visibleContact={true}
            placeHolderText={'Nome do Canal'}
            textLabel={'Nome do Canal'}
            placeHolderText2={'E-mail'}
            textLabel2={'E-mail'}
            contactLabel={'Nome do Contato'}
            contactPlaceholder={'Nome do Contato'}
            visibleNrFone={true}
            modalNrFone={modalPhone}
            setModalNrFone={setModalPhone}
            maskPhone={maskPhone}
            visibleNrCpfCnpj={true}
            modalNrCpfCnpj={modalCpfCnpj}
            setModalNrCpfCnpj={setModalCpfCnpj}
            maskCpfCnpj={maskCpfCnpj}
            modalContact={modalContact}
            setModalContact={setModalContact}
          />
          <ModalEdit
            modalOpen={modalEdit}
            handleClose={() => setModalEdit(false)}
            handleConfirm={(modalText, modalNumber1, modalNumber2, modalNumber3, id, modalStatus, date, modalTextPassword) =>
              editSalesChannel(modalText, modalNumber1, modalNumber2, modalNumber3, id, modalStatus, date, modalTextPassword, modalPhone, modalCpfCnpj, modalContact, modalEmail)
            }
            handleCancel={() => setModalEdit(false)}
            modalTitleText={`Editar Canal de Venda #${id}`}
            visibleText={true}
            visibleText2={true}
            visibleContact={true}
            textLabel={'Nome do Canal'}
            textLabel2={'E-mail'}
            contactLabel={'Nome do Contato'}
            id={id}
            setModalText={setModalName}
            modalText={modalName}
            modalText2={modalEmail}
            setModalText2={setModalEmail}
            modalContact={modalContact}
            setModalContact={setModalContact}
            visibleStatus={true}
            modalStatus={modalSituation}
            setModalStatus={setModalSituation}
            visibleNrFone={true}
            modalNrFone={modalPhone}
            setModalNrFone={setModalPhone}
            maskPhone={maskPhone}
            visibleNrCpfCnpj={true}
            modalNrCpfCnpj={modalCpfCnpj}
            setModalNrCpfCnpj={setModalCpfCnpj}
            maskCpfCnpj={maskCpfCnpj}
          />
          <Footer />
      </Container>
    </>
  );
};

export default SalesChannelList;
